<template>
<div>
  <v-layout v-if="loading" row justify-center>
    <v-flex class="text-xs-center py-5 my-5" xs12>
      <v-progress-circular color="accent" indeterminate></v-progress-circular>
    </v-flex>
  </v-layout>
  <v-layout v-else-if="progress.status == 'START'" row wrap justify-center>
    <v-flex class="py-5 text-xs-center" xs12>
      <div class="display-1 pb-3"><b>Test Preview</b></div>
      <div v-if="taskData.type == 'Practice'" class="headline pb-3">{{taskData.numOfQuestions}} Questions</div>
      <div v-else class="headline pb-3">{{taskData.minQuestions}} To {{taskData.maxQuestions}} Questions</div>
      <div class="headline pb-3"><b>{{taskData.timeAllowed ? taskData.timeAllowed : 'Unlimited'}}</b> Minutes Time Limit</div>
      <v-btn @click.native="startTest" class="title" color="success" large><b>Start</b></v-btn>
    </v-flex>
  </v-layout>
  <v-layout v-else-if="progress.status == 'IN PROGRESS'" row wrap justify-center>
    <v-flex class="pt-3 text-xs-center" xs8 sm5 md4>
      <div class="headline pa-2"><b>{{taskData.timeAllowed ? timeLeftString : 'Unlimited Time'}}</b></div>
    </v-flex>
    <v-flex xs12>
      <div class="headline pa-2"><b>#{{progress.currentQuestion + 1}} - Question ID: {{progress.questions[progress.currentQuestion].id}}</b></div>
      <div @keydown="disableCopy($event)" tabindex="0" class="headline pa-2 mb-3">{{progress.questions[progress.currentQuestion].body}}</div>
    </v-flex>
    <v-flex class="text-xs-center" xs12 md10>
      <v-card
        @click.native="chooseAnswer(i)"
        @mouseover="answer.hover = true"
        @mouseout="answer.hover = false"
        style="cursor: pointer"
        v-for="answer, i in progress.questions[progress.currentQuestion].answers"
        :key="i"
        class="pa-3 mb-3"
        :color="(graded && answer.correct && answer.selected) ? 'success' : (graded && !answer.correct && answer.selected) ? 'error' : answer.selected ? 'grey lighten-2 elevation-1' : answer.hover ? 'grey lighten-4 elevation-0' : 'elevation-0'"
      >
        <v-layout row wrap>
          <v-flex class="pr-2" xs1>
            <div class="headline pt-1">{{answer.letter}}.</div>
          </v-flex>
          <v-flex class="pl-2 text-xs-left" xs11>
            <div class="headline pt-1">
              <span>{{answer.text}} - </span>
              <span v-if="answer.correct" class="success--text">Correct</span>
              <span v-else class="error--text">Wrong</span>
            </div>
          </v-flex>
        </v-layout>
      </v-card>
    </v-flex>
    <v-flex v-if="graded && taskData.type == 'Practice'" class="pb-3 text-xs-center" xs12>
      <div class="headline pb-3">
        <span v-if="progress.questions[progress.currentQuestion].correct" class="success--text"><b>Correct</b></span>
        <span v-else class="error--text"><b>Wrong</b></span>
      </div>
      <div class="headline">
        <span><b>Rationale</b></span><br>
        <span>{{progress.questions[progress.currentQuestion].rationale}}</span>
      </div>
    </v-flex>
    <v-flex class="pb-3 text-xs-center" xs12>
      <v-btn v-if="!graded && taskData.type == 'Practice'" @click.native="gradeQuestion" class="title" color="primary" large><b>Check</b></v-btn>
      <v-btn v-else @click.native="nextQuestion" class="title" color="success" large><b>Next</b></v-btn>
      <v-btn @click.native="resetProgress" class="title" color="error" large><b>Reset</b></v-btn>
    </v-flex>
  </v-layout>
  <v-layout v-else-if="progress.status == 'FINISHED'" row wrap justify-center>
    <v-flex class="py-5 text-xs-center" xs12>
      <div class="display-1 pb-3"><b>Test Grade</b></div>
      <div v-if="taskData.type == 'Practice'" class="headline pb-3">
        <div class="pb-3">
          <b>Score: <span :class="progress.grade > 75 ? 'success--text' : progress.grade > 55 ? 'accent--text' : 'error--text'">
            {{progress.grade + '%'}}
          </span></b>
        </div>
        <div class="title pb-2"><b>Score By {{taskData.type == 'Practice' ? 'Tags' : 'NCLEX Areas'}}</b></div>
        <div v-for="tag in progress.tagsOrAreas" class="subheading pb-2">
          <b>{{tag}}: </b>
          <span :class="progress.tagsOrAreasGrades[tag] < 75 ? 'error--text' : 'success--text'">{{progress.tagsOrAreasGrades[tag]}}%</span>
        </div>
      </div>
      <div v-else class="headline pb-3">
        <div class="pb-3">
          <b>Score: </b>
          <span v-if="progress.pass" class="success--text">Pass</span>
          <span v-else class="error--text">Fail</span>
        </div>
        <div class="pb-3"><b>{{progress.questions.length}} Questions</b></div>
        <div class="title pb-2"><b>Score By Tags</b></div>
        <div v-for="tag in progress.tagsOrAreas" class="subheading pb-2">
          <b>{{tag}}: </b>
          <span v-if="progress.tagsOrAreasGrades[tag] >= 75" class="success--text">{{progress.tagsOrAreasGrades[tag]}}% - Passing</span>
          <span v-else-if="progress.tagsOrAreasGrades[tag] >= 60" class="accent--text">{{progress.tagsOrAreasGrades[tag]}}% - Near Passing</span>
          <span v-else class="error--text">{{progress.tagsOrAreasGrades[tag]}}% - Below Passing</span>
        </div>
      </div>
      <v-btn @click.native="resetProgress" class="title" color="error" large><b>Reset</b></v-btn>
    </v-flex>
  </v-layout>
</div>
</template>

<script>
export default {
  data: () => ({
    loading: false,
    grading: false,
    graded: false,
    timer: null,
    taskData: {},
    progress: {},
    possibleNclex: []
  }),
  computed: {
    now () {
      return this.$store.getters.now
    },
    timeLeftString () {
      const time = new Date(this.progress.timeLeft)
      let string = ''
      let hrs = time.getUTCHours()
      let mins = time.getUTCMinutes()
      let secs = time.getUTCSeconds()
      let hrString = hrs > 9 ? ('' + hrs) : ('0' + hrs)
      let minString = mins > 9 ? ('' + mins) : ('0' + mins)
      let secString = secs > 9 ? ('' + secs) : ('0' + secs)
      string += hrs > 0 ? (hrString + ':') : ''
      string += minString + ':' + secString
      return string
    }
  },
  beforeMount () {
    this.load()
  },
  methods: {
    load () {
      this.loading = true
      const contentData = this.$store.getters.contentByUrl(this.$route.params.contentUrl)
      let sectionData = {}
      for (let s of this.$store.getters.sectionByContent(contentData.key)) {
        if (s.url === this.$route.params.sectionUrl) sectionData = s
      }
      for (let t of this.$store.getters.taskBySection(sectionData.key)) {
        if (t.url === this.$route.params.taskUrl) {
          if (t.type === 'Linking') this.taskData = this.$store.getters.taskByKey(t.linkedTaskKey)
          else this.taskData = t
        }
      }
      this.resetProgress()
      this.loading = false
    },
    resetProgress () {
      this.stopTimer()
      this.graded = false
      this.possibleNclex = []
      this.progress = {
        status: 'START',
        timeLeft: 0,
        startTime: 0,
        endTime: 0,
        questions: [],
        questionKeys: [],
        score: 0,
        grade: 0,
        tagsOrAreas: [],
        tagsOrAreasTotals: {},
        tagsOrAreasScores: {},
        tagsOrAreasGrades: {},
        currentQuestion: 0,
        current: 0,
        expected: 0,
        pass: false
      }
    },
    startTest () {
      this.loading = true
      if (this.taskData.type === 'Practice') this.getQuestions()
      else {
        this.possibleNclexQuestions()
        for (let i = 0; i < this.progress.tagsOrAreas.length; i++) {
          this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]] = 0
          this.progress.tagsOrAreasScores[this.progress.tagsOrAreas[i]] = 0
        }
        this.progress.current = parseInt(this.taskData.minQuestions)
        this.progress.expected = parseInt(this.taskData.minQuestions) * this.progress.tagsOrAreas.length
        this.getNclexQuestions()
      }
      this.startTime()
      this.loading = false
    },
    chooseAnswer (index) {
      if (!this.graded) {
        let questionData = this.progress.questions[this.progress.currentQuestion]
        if (questionData.type === 'Select All That Apply') {
          for (let i = 0; i < questionData.answers.length; i++) {
            if (i === index) questionData.answers[i].selected = !questionData.answers[i].selected
          }
        } else {
          for (let i = 0; i < questionData.answers.length; i++) {
            if (i === index) questionData.answers[i].selected = true
            else questionData.answers[i].selected = false
          }
        }
      }
    },
    gradeQuestion () {
      this.grading = true
      let correct = true
      let answers = this.progress.questions[this.progress.currentQuestion].answers
      for (let i = 0; i < answers.length; i++) {
        if (answers[i].correct !== answers[i].selected) correct = false
      }
      this.progress.questions[this.progress.currentQuestion].correct = correct
      if (correct) {
        this.progress.score++
        for (let i = 0; i < this.progress.tagsOrAreas.length; i++) {
          if (this.taskData.type === 'Practice') {
            if (this.progress.questions[this.progress.currentQuestion].tags.includes(this.progress.tagsOrAreas[i])) {
              this.progress.tagsOrAreasScores[this.progress.tagsOrAreas[i]]++
            }
          } else {
            if (this.progress.questions[this.progress.currentQuestion].area.includes(this.progress.tagsOrAreas[i])) {
              this.progress.tagsOrAreasScores[this.progress.tagsOrAreas[i]]++
            }
          }
        }
      }
      this.grading = false
      this.graded = true
    },
    gradeTest () {
      let passCount = 0
      let failCount = 0
      for (let i = 0; i < this.progress.tagsOrAreas.length; i++) {
        this.progress.tagsOrAreasGrades[this.progress.tagsOrAreas[i]] = Math.floor(this.progress.tagsOrAreasScores[this.progress.tagsOrAreas[i]] / this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]] * 100)
        if (this.progress.tagsOrAreasGrades[this.progress.tagsOrAreas[i]] >= 75) passCount++
        else if (this.progress.tagsOrAreasGrades[this.progress.tagsOrAreas[i]] < 60) failCount++
      }
      this.progress.grade = Math.floor((this.progress.score / this.progress.questions.length) * 100)
      if (this.taskData.type === 'NCLEX') {
        this.progress.pass = (failCount < 2) && (passCount >= this.progress.tagsOrAreas.length - 2)
      }
      this.progress.tagsOrAreas.sort()
    },
    nextQuestion () {
      if (this.taskData.type === 'Practice') {
        if (this.progress.currentQuestion + 1 < parseInt(this.taskData.numOfQuestions)) {
          this.progress.currentQuestion++
          this.graded = false
        } else {
          this.endTest()
        }
      } else {
        let answers = this.progress.questions[this.progress.currentQuestion].answers
        let answered = false
        for (let a of answers) {
          if (a.selected) answered = true
        }
        if (!answered) {
          const appMsg = {
            type: 'error',
            msg: 'Please answer the question first'
          }
          this.$store.dispatch('showAppMsg', appMsg)
          return
        }
        this.gradeQuestion()
        if (this.progress.currentQuestion + 1 < this.progress.questions.length) {
          this.progress.currentQuestion++
          this.graded = false
        } else {
          let passCount = 0
          let failCount = 0
          let continuable = 0
          let score = 0
          for (let i = 0; i < this.progress.tagsOrAreas.length; i++) {
            score = Math.floor((this.progress.tagsOrAreasScores[this.progress.tagsOrAreas[i]] / this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]]) * 100)
            if (score < 75 && (this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]] < parseInt(this.taskData.maxQuestions))) continuable++
            if (score >= 75) passCount++
            else if (score < 60) failCount++
          }
          if (passCount === this.progress.tagsOrAreas.length || failCount >= 2 || continuable === 0) {
            this.endTest()
          } else {
            this.progress.expected += continuable
            this.progress.current++
            this.getNclexQuestions()
            this.progress.currentQuestion++
            this.graded = false
          }
        }
      }
    },
    endTest () {
      this.loading = true
      this.stopTimer()
      this.gradeTest()
      this.progress.status = 'FINISHED'
      this.loading = false
    },
    getNclexQuestions () {
      let random = null
      let q = null
      let qData = null
      let score = null
      while (this.progress.questions.length < this.progress.expected) {
        random = this.$store.getters.rng(0, this.possibleNclex.length - 1)
        q = this.$store.getters.questionByKey(this.possibleNclex[random])
        if (!this.progress.questionKeys.includes(q.key)) {
          qData = {
            ...q,
            correct: false,
            tags: q.tags ? [...q.tags] : [],
            area: q.area ? [...q.area] : [],
            answers: []
          }
          for (let i = 0; i < q.answers.length; i++) {
            qData.answers.push({
              ...q.answers[i],
              hover: false,
              selected: false
            })
          }
          for (let i = 0; i < this.progress.tagsOrAreas.length; i++) {
            if (qData.area.includes(this.progress.tagsOrAreas[i]) && this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]] < this.progress.current) {
              if (this.progress.questions.length >= (this.taskData.minQuestions * this.progress.tagsOrAreas.length)) {
                score = Math.floor((this.progress.tagsOrAreasScores[this.progress.tagsOrAreas[i]] / this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]]) * 100)
                if (score < 75) {
                  this.progress.questions.push(qData)
                  this.progress.questionKeys.push(qData.key)
                  this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]]++
                }
              } else {
                this.progress.questions.push(qData)
                this.progress.questionKeys.push(qData.key)
                this.progress.tagsOrAreasTotals[this.progress.tagsOrAreas[i]]++
              }
            }
          }
        }
      }
    },
    possibleNclexQuestions () {
      const q = this.$store.getters.questions
      this.possibleNclex = []
      this.progress.tagsOrAreas = []
      let totals = {}
      let enough = true
      const appMsg = {
        type: 'error',
        msg: ''
      }
      for (let i = 0; i < q.length; i++) {
        if (q[i].area && q[i].tags.includes(this.taskData.tags)) {
          this.possibleNclex.push(q[i].key)
          for (let j = 0; j < q[i].area.length; j++) {
            if (!this.progress.tagsOrAreas.includes(q[i].area[j])) {
              this.progress.tagsOrAreas.push(q[i].area[j])
              totals[q[i].area[j]] = 1
            } else totals[q[i].area[j]]++
          }
        }
      }
      for (let i = 0; i < this.progress.tagsOrAreas.length; i++) {
        if (totals[this.progress.tagsOrAreas[i]] < parseInt(this.taskData.maxQuestions)) enough = false
      }
      if (!enough) {
        appMsg.msg = 'Not enough questions in some areas'
        this.$store.dispatch('showAppMsg', appMsg)
        this.resetProgress()
      }
    },
    getQuestions () {
      let possible = this.possibleQuestions()
      if (possible) {
        let easy = 0
        let normal = 0
        let q = null
        let qData = null
        let random = null
        let numEasy = 0
        if (this.taskData.percentEasy) {
          numEasy = Math.round(parseInt(this.taskData.numOfQuestions) * (parseInt(this.taskData.percentEasy) / 100))
        }
        while (this.progress.questions.length < this.taskData.numOfQuestions) {
          random = this.$store.getters.rng(0, possible.length - 1)
          q = this.$store.getters.questionByKey(possible[random])
          qData = {
            ...q,
            correct: false,
            tags: q.tags ? [...q.tags] : [],
            area: q.area ? [...q.area] : [],
            answers: []
          }
          for (let i = 0; i < q.answers.length; i++) {
            qData.answers.push({
              ...q.answers[i],
              hover: false,
              selected: false
            })
          }
          if (!this.progress.questionKeys.includes(possible[random])) {
            if (this.taskData.percentEasy) {
              if (q.tags.includes('Easy')) {
                if (numEasy > easy) {
                  this.progress.questions.push(qData)
                  this.progress.questionKeys.push(qData.key)
                  for (let qt of q.tags) {
                    if (!this.progress.tagsOrAreas.includes(qt)) {
                      this.progress.tagsOrAreas.push(qt)
                      this.progress.tagsOrAreasScores[qt] = 0
                      this.progress.tagsOrAreasTotals[qt] = 1
                    } else this.progress.tagsOrAreasTotals[qt]++
                  }
                  easy++
                }
              } else {
                if (parseInt(this.taskData.numOfQuestions) - numEasy > normal) {
                  this.progress.questions.push(qData)
                  this.progress.questionKeys.push(qData.key)
                  for (let qt of q.tags) {
                    if (!this.progress.tagsOrAreas.includes(qt)) {
                      this.progress.tagsOrAreas.push(qt)
                      this.progress.tagsOrAreasScores[qt] = 0
                      this.progress.tagsOrAreasTotals[qt] = 1
                    } else this.progress.tagsOrAreasTotals[qt]++
                  }
                  normal++
                }
              }
            } else {
              this.progress.questions.push(qData)
              this.progress.questionKeys.push(qData.key)
              for (let qt of q.tags) {
                if (!this.progress.tagsOrAreas.includes(qt)) {
                  this.progress.tagsOrAreas.push(qt)
                  this.progress.tagsOrAreasScores[qt] = 0
                  this.progress.tagsOrAreasTotals[qt] = 1
                } else this.progress.tagsOrAreasTotals[qt]++
              }
            }
          }
        }
      }
    },
    possibleQuestions () {
      const q = this.$store.getters.questions
      let possibleEasy = []
      let possibleHard = []
      const appMsg = {
        type: 'error',
        msg: ''
      }
      for (let i = 0; i < q.length; i++) {
        let tagsFound = true
        for (let j = 0; j < this.taskData.tags.length; j++) {
          if (!q[i].tags.includes(this.taskData.tags[j])) tagsFound = false
        }
        if (tagsFound) {
          if (q[i].tags.includes('Easy')) possibleEasy.push(q[i].key)
          else possibleHard.push(q[i].key)
        }
      }
      let numEasy = 0
      if (this.taskData.percentEasy) {
        numEasy = Math.round(parseInt(this.taskData.numOfQuestions) * (parseInt(this.taskData.percentEasy) / 100))
      }
      if (possibleEasy.length + possibleHard.length < parseInt(this.taskData.numOfQuestions)) {
        appMsg.msg = 'Not enough easy or normal questions with those tags; please Edit the test'
        this.$store.dispatch('showAppMsg', appMsg)
        this.load()
        return null
      }
      if (possibleEasy.length < numEasy || possibleHard.length < (parseInt(this.taskData.numOfQuestions) - numEasy)) {
        appMsg.msg = possibleEasy.length < numEasy ? 'Not enough easy questions with those tags; please Edit the test' : 'Not enough normal questions with those tags; please Edit the test'
        this.$store.dispatch('showAppMsg', appMsg)
        this.load()
        return null
      }
      return possibleEasy.concat(possibleHard)
    },
    startTime () {
      this.progress.startTime = this.now
      this.progress.endTime = this.now + (this.taskData.timeAllowed * this.$store.getters.time.minute)
      this.progress.timeLeft = this.progress.endTime - this.progress.startTime
      this.timer = setInterval(() => {
        this.progress.timeLeft -= 1000
        if (this.progress.timeLeft === -1000) {
          this.endTest()
        }
      }, 1000)
      this.progress.status = 'IN PROGRESS'
    },
    stopTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    disableCopy (event) {
      if (event.key === 'c' && event.ctrlKey) event.preventDefault()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/content/TestPreview.vue